import React, { useState } from "react";
import { Table, Tag, Button, Dropdown } from "antd";
import { ArrowUpRight, MoreHorizontal, BookOpen, Video, Pencil } from "lucide-react";
import EditStudentModal from "./EditStudentModal";

const getStatusClass = (status) => {
  switch (status) {
    case "ACTIVE":
      return "text-green-700";
    case "IN_PROGRESS":
      return "text-yellow-700";
    case "COMPLETED":
      return "text-blue-700";
    default:
      return "text-gray-700";
  }
};

const StudentsTable = ({
  studentsData,
  setSelectedUserIds,
  selectedUserIds,
  noCheckboxRequired = false,
  onAssignGuide,
  onAssignMockInterview,
  onRefreshStudents,
  setLoading,
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);

  const handleOpenEdit = (student) => {
    setEditingStudent(student);
    setEditModalOpen(true);
  };
  const rowSelection = {
    selectedRowKeys: selectedUserIds,

    onChange: (selectedRowKeys) => {
      setSelectedUserIds(selectedRowKeys);
    },
  };
  // console.log(selectedUserIds)
  const columns = [
    {
      title: (
        <span className="text-[#475467] font-medium text-[.8rem]">Name</span>
      ),
      dataIndex: "firstName",
      key: "name",
      // width: 120, // Increased width for Name column
      render: (_, record) => {
        const studentUserId = record?.userId || record?.userIdentifier;
        const menuItems = [
          {
            key: "edit-student",
            label: (
              <span className="flex items-center gap-2 font-medium text-[.82rem] text-[#344054] hover:text-[#DC6803]">
                <Pencil size={15} className="text-[#DC6803]" />
                <span>Edit Student</span>
              </span>
            ),
          },
          {
            key: "assign-guide",
            label: (
              <span className="flex items-center gap-2 font-medium text-[.82rem] text-[#344054] hover:text-[#DC6803]">
                <BookOpen size={15} className="text-[#DC6803]" />
                <span>Assign Guide</span>
              </span>
            ),
          },
          {
            key: "assign-mock-interview",
            label: (
              <span className="flex items-center gap-2 font-medium text-[.82rem] text-[#344054] hover:text-[#DC6803]">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 14 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-[#DC6803]"
                >
                  <path
                    d="M4.30313 11.5002H9.10313M6.70313 9.10019V11.5002M3.58313 9.10019H9.82313C10.8312 9.10019 11.3353 9.10019 11.7203 8.90401C12.059 8.73143 12.3344 8.45607 12.5069 8.11738C12.7031 7.73234 12.7031 7.22829 12.7031 6.22019V3.58019C12.7031 2.5721 12.7031 2.06805 12.5069 1.68301C12.3344 1.34432 12.059 1.06896 11.7203 0.896384C11.3353 0.700195 10.8312 0.700195 9.82313 0.700195H3.58313C2.57503 0.700195 2.07098 0.700195 1.68594 0.896384C1.34725 1.06896 1.07189 1.34432 0.899313 1.68301C0.703125 2.06805 0.703125 2.5721 0.703125 3.5802V6.22019C0.703125 7.22829 0.703125 7.73234 0.899313 8.11738C1.07189 8.45607 1.34725 8.73143 1.68594 8.90401C2.07098 9.10019 2.57503 9.10019 3.58313 9.10019Z"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.70312 8.16764C4.48172 7.34184 5.5388 6.83431 6.70313 6.83431C7.86745 6.83431 8.92453 7.34184 9.70313 8.16764M8.20313 4.00098C8.20313 4.8294 7.53155 5.50098 6.70313 5.50098C5.8747 5.50098 5.20313 4.8294 5.20313 4.00098C5.20313 3.17255 5.8747 2.50098 6.70313 2.50098C7.53155 2.50098 8.20313 3.17255 8.20313 4.00098Z"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>{" "}
                <span>Assign Mock Interview</span>
              </span>
            ),
          },
        ];

        const handleMenuClick = ({ key }) => {
          if (key === "edit-student") {
            handleOpenEdit(record);
          } else if (key === "assign-guide" && onAssignGuide) {
            onAssignGuide(studentUserId);
          } else if (key === "assign-mock-interview" && onAssignMockInterview) {
            onAssignMockInterview(studentUserId);
          }
        };

        const studentContent = noCheckboxRequired ? (
          <div>
            <p className="font-semibold text-gray-800 mb-0 text-[.8rem]">
              {record.firstName} {record.lastName}
            </p>
            <p className="text-[.75rem] text-[#475467] font-normal">
              {studentUserId || "N/A"}
            </p>
          </div>
        ) : (
          <div className="group cursor-pointer inline-flex flex-col py-1 px-2 -mx-2 rounded-md transition-all duration-200 hover:bg-[#FFFAEB]/80 border border-transparent hover:border-[#FEDF89]">
            <p className="font-semibold text-gray-800 mb-0 text-[.8rem] transition-colors duration-200 group-hover:text-[#DC6803]">
              {record.firstName} {record.lastName}
            </p>
            <p className="text-[.75rem] text-[#475467] font-normal transition-colors duration-200 group-hover:text-[#B54708]">
              {studentUserId || "N/A"}
            </p>
          </div>
        );

        if (!noCheckboxRequired && (onAssignGuide || onAssignMockInterview)) {
          return (
            <Dropdown
              menu={{
                items: menuItems,
                onClick: handleMenuClick,
              }}
              trigger={["click"]}
              placement="bottomLeft"
            >
              {studentContent}
            </Dropdown>
          );
        }

        return studentContent;
      },
    },
    {
      title: (
        <span className="text-[#475467] font-medium text-[.8rem]">
          Student Id & Email
        </span>
      ),
      align: "center",
      key: "email",

      render: (record) => (
        <div>
          <p className="text-[.8rem] text-[#475467] font-normal">
            {record?.studentId || "N/A"}
          </p>
          <p className="text-[#475467] font-normal text-[.8rem]">
            {record?.email || "N/A"}
          </p>
        </div>
      ),
    },
    {
      title: (
        <span className="text-[#475467] font-medium text-[.8rem]">
          Mobile no.
        </span>
      ),

      key: "phone",
      align: "center",
      render: (record) =>
        noCheckboxRequired ? (
          <div>
            <p className="text-[.8rem] text-[#475467] font-normal mb-0">
              {record?.mobileNumber || "N/A"}
            </p>
          </div>
        ) : (
          <div
            onClick={() => handleOpenEdit(record)}
            className="group inline-flex items-center justify-center gap-1.5 cursor-pointer py-1 px-2 rounded-md hover:bg-[#FFFAEB]/80 border border-transparent hover:border-[#FEDF89] transition-all duration-200"
            title="Click to edit student"
          >
            <span className="text-[.8rem] text-[#475467] font-normal group-hover:text-[#DC6803] transition-colors duration-200">
              {record?.mobileNumber || "N/A"}
            </span>
            <Pencil
              size={12}
              className="text-gray-400 opacity-0 group-hover:opacity-100 group-hover:text-[#DC6803] transition-opacity duration-200"
            />
          </div>
        ),
    },
    // {
    //   title: (
    //     <div className="text-center text-[#475467] font-medium text-[.8rem]">
    //       Prep Guides Assigned
    //     </div>
    //   ),
    //   dataIndex: "prepGuidesAssigned",
    //   key: "prepGuidesAssigned",
    //   align: "center",

    //   render: (count) => (
    //     <span className="font-normal text-[#475467] text-[.8rem]">
    //       {count ?? "N/A"}
    //     </span>
    //   ),
    // },
    // {
    //   title: (
    //     <div className="text-center text-[#475467] font-medium text-[.8rem]">
    //       Mock Interviews Assigned
    //     </div>
    //   ),
    //   dataIndex: "mockInterviewsAssigned",
    //   key: "mockInterviewsAssigned",
    //   align: "center",
    //   render: (count) => (
    //     <span className="font-normal text-[#475467] text-[.8rem]">
    //       {count ?? "N/A"}
    //     </span>
    //   ),
    // },
    // {
    //   title: (
    //     <div className="text-center text-[#475467] font-medium text-[.8rem]">
    //       Practice Answer Credits
    //     </div>
    //   ),
    //   dataIndex: "practiceAnswersCreditsLeft",
    //   key: "practiceAnswersCreditsLeft",
    //   align: "center",

    //   render: (count) => (
    //     <span className="font-normal text-[#475467] text-[.8rem]">
    //       {count ?? "N/A"}
    //     </span>
    //   ),
    // },

    {
      title: (
        <div className="text-center text-[#475467] font-medium text-[.8rem]">
          Added On
        </div>
      ),
      dataIndex: "creationDate",
      key: "creationDate",
      align: "center",
      // width: 40,
      render: (date) => (
        <span className="text-[#475467] font-normal text-[.8rem]">
          {new Date(date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    // {
    //   title: (
    //     <div className="text-center text-[#475467] font-medium text-[.8rem]">
    //       Action
    //     </div>
    //   ),
    //   key: "action",
    //   align: "center",
    //   render: (_, record) => (
    //     <div className="flex items-center justify-center">
    //       <button
    //         type="button"
    //         title="Edit Student"
    //         onClick={() => handleOpenEdit(record)}
    //         className="p-1.5 rounded-md text-gray-500 hover:text-[#DC6803] hover:bg-[#FFFAEB] transition-colors duration-150"
    //       >
    //         <Pencil size={15} />
    //       </button>
    //     </div>
    //   ),
    // },
  ];

  return (
    <div className="rounded-2xl bg-white shadow-sm">
      <Table
        columns={columns}
        dataSource={studentsData}
        // rowKey="batchName"
        rowKey="userId"
        pagination={false}
        rowSelection={
          noCheckboxRequired
            ? undefined
            : {
                type: "checkbox",
                ...rowSelection,
              }
        }
        scroll={{ x: 1000 }}
      />
      {editModalOpen && (
        <EditStudentModal
          open={editModalOpen}
          setOpen={setEditModalOpen}
          student={editingStudent}
          onSuccess={onRefreshStudents}
          setLoading={setLoading}
        />
      )}
    </div>
  );
};

export default StudentsTable;
